/**
 * Translation Hook
 * Wrapper around react-i18next with game-specific helpers
 */

import { useTranslation as useReactI18n } from 'react-i18next';
import type { Language } from '../i18n/config';

export function useTranslation() {
  const { t, i18n } = useReactI18n();

  const changeLanguage = (lang: Language) => {
    i18n.changeLanguage(lang);
  };

  const currentLanguage = i18n.language as Language;

  // Helper to get translated labels for screens
  const getGameLabels = () => ({
    botHand: t('game.botHand'),
    yourHand: t('game.yourHand'),
    trump: t('game.trump'),
    deck: t('game.deck'),
    draw: t('game.draw'),
    discarded: t('game.discarded'),
    playZone: t('game.playZone'),
    pass: t('game.actions.pass'),
    take: t('game.actions.take'),
    tracker: t('game.actions.tracker'),
    playArea: t('game.playArea'),
    player: t('game.player'),
    you: t('game.you'),
    xpProgress: t('game.xpProgress'),
    aria: {
      board: t('game.aria.board'),
      yourHandList: t('game.aria.yourHandList'),
      botHandFaceDown: t('game.aria.botHandFaceDown'),
      playZone: t('game.aria.playZone'),
      deckArea: t('game.aria.deckArea'),
      drawPile: (count: number) => t('game.aria.drawPile', { count }),
      discardPile: (count: number) => t('game.aria.discardPile', { count }),
      takeCards: t('game.aria.takeCards'),
      passTurn: t('game.aria.passTurn'),
      tracker: t('game.aria.tracker'),
      xpProgress: t('game.aria.xpProgress'),
    },
  });

  const getQuestionLabels = () => ({
    submit: t('question.actions.submit'),
    next: t('question.actions.next'),
    hint: t('question.hint.button'),
    extend: t('question.timer.extend'),
    correct: t('question.feedback.correct'),
    wrong: t('question.feedback.wrong'),
    instruction: t('question.instruction'),
    timeLimit: (time: number) => t('question.timeLimit', { time }),
    cardLabel: (rank: string, suit: string) => t('question.cardLabel', { rank, suit }),
    difficulty: {
      easy: t('question.difficulty.easy'),
      medium: t('question.difficulty.medium'),
      hard: t('question.difficulty.hard'),
    },
    tutorial: {
      title: t('question.tutorial.title'),
      line1: t('question.tutorial.line1'),
      line2Strong: t('question.tutorial.line2Strong'),
      line2Rest: t('question.tutorial.line2Rest'),
      dismiss: t('question.tutorial.dismiss'),
    },
    hintUsed: t('question.hint.used'),
    perspectiveLt: t('question.feedback.perspectiveLt'),
    perspectiveBe: t('question.feedback.perspectiveBe'),
    aria: {
      timerRemaining: t('question.timer.ariaRemaining'),
      timer: t('question.timer.ariaTimer'),
      extendTimer: t('question.timer.ariaExtend'),
      era: t('question.aria.era'),
      hintUsed: t('question.hint.ariaUsed'),
      hintUse: t('question.hint.ariaUse'),
      submit: t('question.aria.submit'),
      continue: t('question.aria.continue'),
    },
  });

  const getPerspectiveLabels = () => ({
    title: t('perspective.title'),
    subtitle: t('perspective.subtitle'),
    sourceCheck: t('perspective.sourceCheck'),
    skip: t('perspective.actions.skip'),
    confirm: t('perspective.actions.confirm'),
    options: [
      t('perspective.options.lt'),
      t('perspective.options.be'),
      t('perspective.options.neutral'),
    ] as [string, string, string],
    topic: t('perspective.topic'),
    source: t('perspective.source'),
    ltTitle: t('perspective.ltTitle'),
    beTitle: t('perspective.beTitle'),
    aria: {
      columns: t('perspective.aria.columns'),
      ltColumn: t('perspective.aria.ltColumn'),
      beColumn: t('perspective.aria.beColumn'),
      neutralNote: t('perspective.aria.neutralNote'),
      sourceOptions: t('perspective.aria.sourceOptions'),
      skip: t('perspective.aria.skip'),
      confirm: t('perspective.aria.confirm'),
    },
  });

  const getReviewLabels = () => ({
    youWin: t('review.results.win'),
    youLose: t('review.results.lose'),
    botDurak: t('review.results.botDurak'),
    youDurak: t('review.results.youDurak'),
    review: t('review.srs.title'),
    xpEarned: t('review.stats.xp'),
    correct: t('review.stats.correct'),
    streak: t('review.stats.streak'),
    time: t('review.stats.time'),
    playAgain: t('review.actions.playAgain'),
    share: t('review.actions.share'),
    tracker: t('review.actions.tracker'),
    eraTitle: t('review.era.title'),
    again: t('review.srs.again'),
    hard: t('review.srs.hard'),
    good: t('review.srs.good'),
    easy: t('review.srs.easy'),
    nextReview: t('review.srs.nextReview'),
    mastered: t('review.srs.mastered'),
    reviewNeeded: t('review.srs.reviewNeeded'),
    eraSegments: {
      medieval: t('review.era.medieval'),
      earlyModern: t('review.era.earlyModern'),
      modern: t('review.era.modern'),
    },
    due: {
      title: t('review.due.title'),
      count: (count: number) => t('review.due.count', { count }),
      reps: (count: number) => t('review.due.reps', { count }),
      more: (count: number) => t('review.due.more', { count }),
    },
    notice: {
      due: (count: number) => t('review.notice.due', { count }),
      caughtUp: t('review.notice.caughtUp'),
    },
    aria: {
      session: t('review.aria.session'),
      stats: t('review.aria.stats'),
      byEra: t('review.aria.byEra'),
      eraDistribution: t('review.aria.eraDistribution'),
      dueCards: t('review.aria.dueCards'),
      rateRecall: t('review.aria.rateRecall'),
      srsSection: t('review.aria.srsSection'),
      playAgain: t('review.aria.playAgain'),
      share: t('review.aria.share'),
      viewTracker: t('review.aria.viewTracker'),
    },
  });

  const getTrackerLabels = () => ({
    title: t('tracker.title'),
    subtitle: t('tracker.subtitle'),
    backToGame: t('tracker.actions.backToGame'),
    backToReview: t('tracker.actions.backToReview'),
    locked: t('tracker.status.locked'),
    aria: {
      screen: t('tracker.aria.screen'),
      eraItem: (name: string, percent: number, locked: boolean) =>
        locked
          ? t('tracker.aria.eraItemLocked', { name, percent })
          : t('tracker.aria.eraItem', { name, percent }),
      eraProgress: (percent: number) => t('tracker.aria.eraProgress', { percent }),
      backToGame: t('tracker.aria.backToGame'),
      backToReview: t('tracker.aria.backToReview'),
    },
  });

  return {
    t,
    i18n,
    changeLanguage,
    currentLanguage,
    getGameLabels,
    getQuestionLabels,
    getPerspectiveLabels,
    getReviewLabels,
    getTrackerLabels,
  };
}
